import React, {useRef, useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Button, EMPHASIS, Icon, SubHeading} from '@forecast-it/design-system';
import {CommitMutation} from '../../../mutations/CommitMutation';
import CreateBaselineVersionPhaseMutation from './CreateBaselineVersionPhaseMutation';
import {C, F} from './versionsStyles';

export interface EmptyVersionStateProps {
	projectId: string;
	baselineVersionId: string;
	locked: boolean;
	onPhaseAdded: () => void;
}

const Box = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 10px;
	box-sizing: border-box;
	padding: 48px 24px;
	text-align: center;
	border: 1px dashed ${C.border};
	border-radius: 8px;
`;

const Body = styled.span`
	${F.body2}
	max-width: 360px;
	color: ${C.aux};
`;

export const EmptyVersionState = ({projectId, baselineVersionId, locked, onPhaseAdded}: EmptyVersionStateProps) => {
	const {formatMessage} = useIntl();
	const [saving, setSaving] = useState(false);
	const inFlight = useRef(false);

	const finish = () => {
		inFlight.current = false;
		setSaving(false);
	};

	const addPhase = () => {
		if (locked || inFlight.current) {
			return;
		}
		inFlight.current = true;
		setSaving(true);
		CommitMutation(
			CreateBaselineVersionPhaseMutation,
			{projectId, baselineVersionId, name: formatMessage({id: 'baseline.versions.new_phase_name'})},
			() => {
				finish();
				onPhaseAdded();
			},
			false,
			null,
			(error: Error, onFailure?: (e: Error) => void) => {
				finish();
				onFailure?.(error);
			}
		);
	};

	return (
		<Box data-cy="baseline-version-empty">
			<Icon icon="object" size="l" color="primary" />
			<SubHeading size="2">{formatMessage({id: 'baseline.versions.empty_title'})}</SubHeading>
			<Body>{formatMessage({id: 'baseline.versions.empty_body'})}</Body>
			<Button emphasis={EMPHASIS.HIGH} disabled={locked || saving} onPress={addPhase}>
				{formatMessage({id: 'baseline.versions.add_phase'})}
			</Button>
		</Box>
	);
};
